import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiService } from '../services/api';
import { useAuthStore } from '../store/auth';
import Loader from '../components/Loader';

interface UserProfile {
  id: number;
  telegramId: number;
  firstName: string;
  lastName?: string;
  username?: string;
  rating: number;
  reviewsCount: number;
  dealsCount: number;
  isPremium: boolean;
  createdAt: string;
}

interface Review {
  id: string;
  rating: number;
  comment: string;
  createdAt: string;
  reviewer: {
    firstName: string;
  };
}

export default function Profile() {
  const { id } = useParams<{ id: string }>();
  const { user: currentUser } = useAuthStore();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'listings' | 'reviews'>('listings');
  const [listings, setListings] = useState<any[]>([]);

  const isOwnProfile = !id || (currentUser && currentUser.telegramId === parseInt(id));
  const displayUser = isOwnProfile ? currentUser : profile;

  useEffect(() => {
    if (isOwnProfile) {
      setProfile(currentUser as any);
      loadUserData(currentUser?.id);
    } else {
      loadProfile();
    }
  }, [id, currentUser]);

  const loadProfile = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const response = await apiService.getUser(Number(id));
      setProfile(response.data.data);
      loadUserData(response.data.data.id);
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadUserData = async (userId?: number) => {
    if (!userId) {
      setIsLoading(false);
      return;
    }
    
    try {
      const [listingsRes, reviewsRes] = await Promise.all([
        apiService.getListings({ userId, status: 'active' }),
        apiService.getUserReviews(userId)
      ]);
      setListings(listingsRes.data.data || []);
      setReviews(reviewsRes.data.data || []);
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <span key={i} className={i < rating ? 'text-yellow-400' : 'text-gray-300'}>
        ★
      </span>
    ));
  };

  if (isLoading && !displayUser) {
    return <Loader className="py-12" />;
  }

  if (!displayUser) {
    return (
      <div className="text-center py-8">
        <p className="text-tg-theme-hint">Пользователь не найден</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Profile Header */}
      <div className="text-center">
        <div className="w-24 h-24 rounded-full bg-tg-theme-button mx-auto flex items-center justify-center text-white text-3xl">
          {displayUser.firstName[0]}
        </div>
        <h1 className="text-xl font-bold mt-3">
          {displayUser.firstName} {displayUser.lastName}
        </h1>
        {displayUser.username && (
          <p className="text-tg-theme-hint">@{displayUser.username}</p>
        )}
        {displayUser.isPremium && (
          <span className="inline-block bg-yellow-400 text-yellow-900 px-2 py-1 rounded-full text-xs font-medium mt-2">
            ⭐️ Premium
          </span>
        )}
      </div>

      {/* Stats */}
      <div className="card">
        <div className="flex items-center justify-around">
          <div className="text-center">
            <p className="text-2xl font-bold">⭐️ {displayUser.rating.toFixed(1)}</p>
            <p className="text-xs text-tg-theme-hint">Рейтинг</p>
          </div>
          <div className="h-8 w-px bg-tg-theme-hint/20" />
          <div className="text-center">
            <p className="text-2xl font-bold">{displayUser.dealsCount || 0}</p>
            <p className="text-xs text-tg-theme-hint">Сделок</p>
          </div>
          <div className="h-8 w-px bg-tg-theme-hint/20" />
          <div className="text-center">
            <p className="text-2xl font-bold">{displayUser.reviewsCount || 0}</p>
            <p className="text-xs text-tg-theme-hint">Отзывов</p>
          </div>
        </div>
      </div>

      {/* Actions */}
      {isOwnProfile ? (
        <div className="grid grid-cols-2 gap-2">
          <Link to="/listings/create" className="btn-primary text-center">
            ➕ Объявление
          </Link>
          <Link to="/subscription" className="btn-secondary text-center">
            💎 Подписка
          </Link>
        </div>
      ) : (
        <button className="btn-primary w-full">
          💬 Написать сообщение
        </button>
      )}

      {/* Tabs */}
      <div className="flex border-b border-tg-theme-hint/20">
        <button
          onClick={() => setActiveTab('listings')}
          className={`flex-1 py-3 text-center font-medium ${
            activeTab === 'listings'
              ? 'text-tg-theme-button border-b-2 border-tg-theme-button'
              : 'text-tg-theme-hint'
          }`}
        >
          Объявления ({listings.length})
        </button>
        <button
          onClick={() => setActiveTab('reviews')}
          className={`flex-1 py-3 text-center font-medium ${
            activeTab === 'reviews'
              ? 'text-tg-theme-button border-b-2 border-tg-theme-button'
              : 'text-tg-theme-hint'
          }`}
        >
          Отзывы ({reviews.length})
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'listings' && (
        <div className="space-y-3">
          {listings.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-tg-theme-hint">Объявлений пока нет</p>
            </div>
          ) : (
            listings.map((listing) => (
              <Link key={listing.id} to={`/listings/${listing.id}`}>
                <div className="card hover:opacity-90">
                  <h3 className="font-semibold">{listing.title}</h3>
                  {listing.price && (
                    <p className="text-tg-theme-button font-bold">
                      {listing.price.toLocaleString()} {listing.currency === 'RUB' ? '₽' : listing.currency}
                    </p>
                  )}
                </div>
              </Link>
            ))
          )}
        </div>
      )}

      {activeTab === 'reviews' && (
        <div className="space-y-3">
          {reviews.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-tg-theme-hint">Отзывов пока нет</p>
            </div>
          ) : (
            reviews.map((review) => (
              <div key={review.id} className="card">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold">{review.reviewer.firstName}</span>
                  <span className="text-lg">{renderStars(review.rating)}</span>
                </div>
                <p className="text-sm">{review.comment}</p>
                <p className="text-xs text-tg-theme-hint mt-2">
                  {new Date(review.createdAt).toLocaleDateString('ru-RU')}
                </p>
              </div>
            ))
          )}
        </div>
      )}

      {/* Member Since */}
      <p className="text-sm text-tg-theme-hint text-center">
        На Бирже с {new Date((displayUser as UserProfile).createdAt || Date.now()).toLocaleDateString('ru-RU')}
      </p>
    </div>
  );
}
